/**
 * Definition-driven form engine for the international job applications added
 * after the agent form (Marketing first). Client-safe: no DB access.
 *
 * A step is an ordered list of items: questions (fields) and upload slots.
 * The public step-save route and the wizard both call validateFormStep with
 * the same definitions, so required / conditional rules cannot drift between
 * the browser and the server. The agent application keeps its own, simpler
 * definitions (intl-application.ts) and is not touched by this engine.
 *
 * Storage: a field with storage 'column' is a column of
 * recruitment_intl_applications (shared with the agent form, so the inbox, the
 * IT review and the risk map read every vacancy alike); storage 'extra' lives
 * in the extra_answers JSONB under the field name.
 */
import {
  DURATION_MAX_MINUTES,
  formatDurationMinutes,
} from '@/lib/recruitment/equipment-backup-options';

export type FormRole = {
  company: string;
  title: string;
  start_date: string;
  current: boolean;
  end_date: string | null;
  responsibilities: string;
  reason_for_leaving: string | null;
};

export type FormValue = string | number | boolean | string[] | FormRole[] | null;
export type FormValues = Record<string, FormValue>;

export type FormCondition =
  | { field: string; equals: string | boolean }
  | { field: string; in: readonly string[] }
  /** Multi-select contains this option. */
  | { field: string; includes: string }
  /** Multi-select has at least one option other than this one (e.g. anything but "None"). */
  | { field: string; anyExcept: string }
  /** A file of this kind is uploaded. */
  | { file: string }
  | { all: readonly FormCondition[] }
  | { any: readonly FormCondition[] };

export type FormFieldType =
  | 'string'
  | 'text'
  | 'bool'
  | 'number'
  | 'date'
  | 'choice'
  | 'multi'
  | 'roles'
  /** Hours and minutes, stored as whole minutes (battery life, backup runtime). */
  | 'duration';

export type FormFieldDef = {
  /** Payload key; the column name for storage 'column', else the key in extra_answers. */
  name: string;
  storage: 'column' | 'extra';
  label: string;
  type: FormFieldType;
  /** Required whenever the field is shown. */
  required?: boolean;
  /** Required only while this condition holds (optional otherwise). */
  requiredWhen?: FormCondition;
  /** Shown (and saved) only when the condition holds; hidden fields are stored as NULL. */
  showWhen?: FormCondition;
  options?: readonly string[];
  /** Display labels for stored option values ('1_week' -> 'Within 1 week'). */
  optionLabels?: Record<string, string>;
  /** Multi-select: this option cannot be combined with the others ("None"). */
  exclusiveOption?: string;
  maxLen?: number;
  min?: number;
  max?: number;
  help?: string;
  placeholder?: string;
  inputType?: 'email' | 'tel' | 'url';
  /** Word-count range enforced on save (written assessment). */
  words?: { min: number; max: number };
  /** Text fields: a tick box that answers with this text ("I do not know"). */
  unknownOption?: string;
  /** String fields: a tick box that copies another field ("Same as my phone number"). */
  copyFrom?: { field: string; label: string };
  rows?: number;
};

export type FormFileAccept = 'image' | 'image_pdf' | 'document' | 'pdf_image';

export type FormFileSlot = {
  /** recruitment_intl_files.kind */
  file: string;
  label: string;
  hint?: string;
  accept: FormFileAccept;
  /** true = always required; a condition = required (and shown) only when it holds. */
  required?: boolean | FormCondition;
  /** Shown only when the condition holds (defaults to the required condition). */
  showWhen?: FormCondition;
  /** Opens the front camera on phones. */
  capture?: 'user' | 'environment';
};

export type FormNote = {
  note: string;
  /** Shown only when the condition holds (a block heading of conditional questions). */
  showWhen?: FormCondition;
  /** Rendered as a small block heading instead of a paragraph. */
  heading?: boolean;
};

export type FormStepItem = FormFieldDef | FormFileSlot | FormNote;

export function isFileSlot(item: FormStepItem): item is FormFileSlot {
  return 'file' in item;
}

export function isNote(item: FormStepItem): item is FormNote {
  return 'note' in item;
}

export function isField(item: FormStepItem): item is FormFieldDef {
  return 'name' in item;
}

export function stepFields(items: readonly FormStepItem[]): FormFieldDef[] {
  return items.filter(isField);
}

export function stepFileSlots(items: readonly FormStepItem[]): FormFileSlot[] {
  return items.filter(isFileSlot);
}

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------

export function conditionHolds(
  condition: FormCondition,
  values: FormValues,
  uploadedKinds: ReadonlySet<string>,
): boolean {
  if ('all' in condition) return condition.all.every((c) => conditionHolds(c, values, uploadedKinds));
  if ('any' in condition) return condition.any.some((c) => conditionHolds(c, values, uploadedKinds));
  if ('file' in condition) return uploadedKinds.has(condition.file);
  const value = values[condition.field];
  if ('equals' in condition) return value === condition.equals;
  if ('in' in condition) return typeof value === 'string' && condition.in.includes(value);
  if ('includes' in condition) {
    return Array.isArray(value) && (value as unknown[]).includes(condition.includes);
  }
  return (
    Array.isArray(value) &&
    (value as unknown[]).some((v) => typeof v === 'string' && v !== condition.anyExcept)
  );
}

export function isFieldShown(
  def: FormFieldDef,
  values: FormValues,
  uploadedKinds: ReadonlySet<string>,
): boolean {
  return !def.showWhen || conditionHolds(def.showWhen, values, uploadedKinds);
}

/** Required right now (the field is assumed to be shown). */
export function isFieldRequired(
  def: FormFieldDef,
  values: FormValues,
  uploadedKinds: ReadonlySet<string>,
): boolean {
  if (def.required) return true;
  return !!def.requiredWhen && conditionHolds(def.requiredWhen, values, uploadedKinds);
}

export function isNoteShown(
  note: FormNote,
  values: FormValues,
  uploadedKinds: ReadonlySet<string>,
): boolean {
  return !note.showWhen || conditionHolds(note.showWhen, values, uploadedKinds);
}

export function isFileSlotShown(
  slot: FormFileSlot,
  values: FormValues,
  uploadedKinds: ReadonlySet<string>,
): boolean {
  const condition =
    slot.showWhen ?? (typeof slot.required === 'object' ? slot.required : undefined);
  return !condition || conditionHolds(condition, values, uploadedKinds);
}

export function isFileSlotRequired(
  slot: FormFileSlot,
  values: FormValues,
  uploadedKinds: ReadonlySet<string>,
): boolean {
  if (slot.required === true) return true;
  if (!slot.required) return false;
  return conditionHolds(slot.required, values, uploadedKinds);
}

// ---------------------------------------------------------------------------
// Coercion + validation (shared by the wizard and the step-save route)
// ---------------------------------------------------------------------------

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isRealDate(raw: string): boolean {
  if (!DATE_RE.test(raw)) return false;
  const [y, m, d] = raw.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export const FORM_ROLES_MAX = 10;

function cleanText(raw: unknown, maxLen: number): string | null {
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  return v ? v.slice(0, maxLen) : null;
}

/** One employment-history role; null when a required part is missing or invalid. */
function coerceRole(raw: unknown): FormRole | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const company = cleanText(r.company, 255);
  const title = cleanText(r.title, 255);
  const responsibilities = cleanText(r.responsibilities, 1500);
  const startDate = typeof r.start_date === 'string' && isRealDate(r.start_date) ? r.start_date : null;
  if (!company || !title || !responsibilities || !startDate || typeof r.current !== 'boolean') {
    return null;
  }
  let endDate: string | null = null;
  if (!r.current) {
    if (typeof r.end_date !== 'string' || !isRealDate(r.end_date)) return null;
    endDate = r.end_date;
  }
  return {
    company,
    title,
    start_date: startDate,
    current: r.current,
    end_date: endDate,
    responsibilities,
    reason_for_leaving: r.current ? null : cleanText(r.reason_for_leaving, 1000),
  };
}

type Coerced = { ok: true; value: FormValue } | { ok: false };

/** Strict coercion against the field definition; absent / empty -> null. */
export function coerceFormValue(def: FormFieldDef, raw: unknown): Coerced {
  if (raw === undefined || raw === null || raw === '') return { ok: true, value: null };
  switch (def.type) {
    case 'string':
    case 'text': {
      if (typeof raw !== 'string') return { ok: false };
      const v = raw.trim();
      if (!v) return { ok: true, value: null };
      return { ok: true, value: v.slice(0, def.maxLen ?? (def.type === 'text' ? 3000 : 255)) };
    }
    case 'choice': {
      if (typeof raw !== 'string') return { ok: false };
      const v = raw.trim();
      if (!v) return { ok: true, value: null };
      return def.options?.includes(v) ? { ok: true, value: v } : { ok: false };
    }
    case 'bool':
      return typeof raw === 'boolean' ? { ok: true, value: raw } : { ok: false };
    case 'number': {
      if (typeof raw !== 'number' || !Number.isFinite(raw)) return { ok: false };
      if (def.min !== undefined && raw < def.min) return { ok: false };
      if (def.max !== undefined && raw > def.max) return { ok: false };
      return { ok: true, value: raw };
    }
    case 'duration': {
      // Whole minutes, never negative; the wizard converts hours + minutes.
      if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return { ok: false };
      const minutes = Math.round(raw);
      if (minutes > (def.max ?? DURATION_MAX_MINUTES)) return { ok: false };
      return { ok: true, value: minutes };
    }
    case 'date':
      return typeof raw === 'string' && isRealDate(raw) ? { ok: true, value: raw } : { ok: false };
    case 'multi': {
      if (!Array.isArray(raw)) return { ok: false };
      const out: string[] = [];
      for (const item of raw) {
        if (typeof item !== 'string') return { ok: false };
        const v = item.trim();
        if (!v || out.includes(v)) continue;
        if (def.options && !def.options.includes(v)) return { ok: false };
        out.push(v);
      }
      // "None" cannot be combined with other answers - it wins when both arrive.
      if (def.exclusiveOption && out.includes(def.exclusiveOption) && out.length > 1) {
        return { ok: true, value: [def.exclusiveOption] };
      }
      return { ok: true, value: out.length > 0 ? out : null };
    }
    case 'roles': {
      if (!Array.isArray(raw)) return { ok: false };
      if (raw.length === 0) return { ok: true, value: null };
      if (raw.length > FORM_ROLES_MAX) return { ok: false };
      const roles: FormRole[] = [];
      for (const item of raw) {
        const role = coerceRole(item);
        if (!role) return { ok: false };
        roles.push(role);
      }
      return { ok: true, value: roles };
    }
  }
}

export type FormStepValidation =
  | { ok: true; values: FormValues }
  | { ok: false; missing: string[] };

/**
 * Validates one step: coerces every field, hides what its condition hides
 * (stored as NULL), then checks required answers, word ranges and required
 * uploads. `missing` holds the labels to show the applicant.
 */
export function validateFormStep(
  items: readonly FormStepItem[],
  payload: Record<string, unknown>,
  uploadedKinds: ReadonlySet<string>,
): FormStepValidation {
  const fields = stepFields(items);
  const missing: string[] = [];
  const invalid = new Set<string>();
  const values: FormValues = {};

  for (const def of fields) {
    const res = coerceFormValue(def, payload[def.name]);
    if (!res.ok) {
      invalid.add(def.name);
      values[def.name] = null;
      continue;
    }
    values[def.name] = res.value;
  }

  // In definition order, so a field hidden by an earlier answer is already
  // NULL when a later condition reads it.
  for (const def of fields) {
    if (!isFieldShown(def, values, uploadedKinds)) {
      values[def.name] = null;
      continue;
    }
    if (invalid.has(def.name)) {
      missing.push(def.label);
      continue;
    }
    const v = values[def.name];
    const empty = v === null || v === undefined || (Array.isArray(v) && v.length === 0);
    if (empty) {
      if (isFieldRequired(def, values, uploadedKinds)) missing.push(def.label);
      continue;
    }
    if (def.words && typeof v === 'string') {
      const n = countWords(v);
      if (n < def.words.min || n > def.words.max) {
        missing.push(`${def.label} (${def.words.min}-${def.words.max} words, currently ${n})`);
      }
    }
  }

  for (const slot of stepFileSlots(items)) {
    if (isFileSlotRequired(slot, values, uploadedKinds) && !uploadedKinds.has(slot.file)) {
      missing.push(`${slot.label} (upload)`);
    }
  }

  return missing.length > 0 ? { ok: false, missing } : { ok: true, values };
}

/** Text shown to HR / in the review step for a stored value. */
export function formatFormValue(def: FormFieldDef, value: FormValue | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (def.type === 'bool') return value === true ? 'Yes' : 'No';
  if (def.type === 'multi') {
    return Array.isArray(value) ? (value as string[]).map((v) => def.optionLabels?.[v] ?? v).join(', ') : '';
  }
  if (def.type === 'choice') return def.optionLabels?.[String(value)] ?? String(value);
  if (def.type === 'duration') return formatDurationMinutes(value);
  if (def.type === 'roles') {
    return Array.isArray(value) ? `${value.length} role${value.length === 1 ? '' : 's'}` : '';
  }
  return String(value);
}
